import { useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import BlockIcon from '@mui/icons-material/Block'
import { useTranslation } from 'react-i18next'
import { useStaffList, useCreateStaff, useDeactivateStaff } from '../shared/api/users'
import { errorMessage } from '../shared/api/errorMessage'
import { isEmail, isPhone, isPersonName } from '../shared/validation'
import { useConfirm } from '../shared/components/ConfirmDialog'
import { useOwnerAuth } from './OwnerAuthContext'
import type { CreateStaffPayload, StaffUser } from '../shared/types'

/** OWNER-only staff management (§16 Phase 6c): list, add, and deactivate staff users. */
export default function StaffPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useOwnerAuth()
  const { data: staff, isLoading, isError } = useStaffList(isAuthenticated)
  const [addOpen, setAddOpen] = useState(false)

  return (
    <Container maxWidth="sm" sx={{ py: 2, pb: 8 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('owner.staff.title')}
        </Typography>
        <Button variant="contained" onClick={() => setAddOpen(true)}>
          {t('owner.staff.addStaff')}
        </Button>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Typography color="error">{t('owner.staff.loadError')}</Typography>
      ) : (
        <Stack spacing={1}>
          {(staff ?? []).map((user) => (
            <StaffRow key={user.id} user={user} />
          ))}
        </Stack>
      )}

      {addOpen && <AddStaffDialog onClose={() => setAddOpen(false)} />}
    </Container>
  )
}

function StaffRow({ user }: { user: StaffUser }) {
  const { t } = useTranslation()
  const { session } = useOwnerAuth()
  const confirm = useConfirm()
  const deactivate = useDeactivateStaff()
  // The server 409s on self-deactivation (UserService#deactivate) — don't offer the button at all.
  // Email is unique per tenant (uq_app_user_tenant_email) and this list is tenant-scoped, so it identifies the caller.
  const isSelf = !!user.email && session?.email?.toLowerCase() === user.email.toLowerCase()

  const onDeactivate = async () => {
    if (!(await confirm({
      title: t('owner.staff.deactivateUser'),
      message: t('owner.staff.deactivateConfirm', { email: user.email }),
    }))) return
    deactivate.mutate(user.id, {
      onError: (err) => window.alert(errorMessage(err, t('owner.staff.deactivateError'))),
    })
  }

  return (
    <Card variant="outlined" sx={{ opacity: user.active ? 1 : 0.6 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography fontWeight={700} noWrap>
              {user.name || user.email}
            </Typography>
            {user.name && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                label={t(`owner.roles.${user.role}`, { defaultValue: user.role })}
                size="small"
                color={user.role === 'OWNER' ? 'secondary' : 'default'}
              />
              {!user.active && <Chip label={t('owner.staff.inactive')} size="small" variant="outlined" />}
            </Stack>
          </Box>
          {user.active && !isSelf && (
            <IconButton
              aria-label={t('owner.staff.deactivateUser')}
              color="error"
              disabled={deactivate.isPending}
              onClick={onDeactivate}
            >
              <BlockIcon />
            </IconButton>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

function AddStaffDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const create = useCreateStaff()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState<CreateStaffPayload['role']>('STAFF')

  // Email is optional (staff log in by phone OTP, D-084) — only validate it when one is typed.
  const emailValid = email.trim() === '' || isEmail(email)
  const nameValid = name.trim().length > 0 && isPersonName(name)
  const phoneValid = phone.trim().length > 0 && isPhone(phone)
  const canSave = emailValid && nameValid && phoneValid && !create.isPending

  const submit = () => {
    if (!canSave) return
    create.mutate(
      { email: email.trim(), name: name.trim(), phone: phone.trim(), role },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('owner.staff.addStaff')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {create.isError && <Alert severity="error">{errorMessage(create.error, t('owner.staff.createError'))}</Alert>}
          <TextField
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            required
            error={name !== '' && !nameValid}
            helperText={name !== '' && !nameValid ? t('validation.name') : ' '}
          />
          <TextField
            label={t('common.emailOptional')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            error={email !== '' && !emailValid}
            helperText={email !== '' && !emailValid ? t('validation.email') : ' '}
          />
          <TextField
            label={t('common.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            required
            error={phone !== '' && !phoneValid}
            inputProps={{ inputMode: 'tel' }}
            helperText={phone !== '' && !phoneValid ? t('validation.phone') : t('owner.staff.phoneHint')}
          />
          <TextField select label={t('owner.staff.role')} value={role} onChange={(e) => setRole(e.target.value as CreateStaffPayload['role'])} fullWidth>
            {/* STAFF only: a second owner is a rare, high-blast-radius account, not something to
                create by mistake from a dropdown. Seed one by hand (docs/onboarding.html) if a
                shop really needs two. RIDER lands here when it arrives (§9). */}
            <MenuItem value="STAFF">{t('owner.roles.STAFF')}</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={!canSave}>
          {t('owner.staff.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
